/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

/**
 * Bridge to the MyGeotab add-in shim.
 *
 * When Insights runs as a MyGeotab add-in the page sits in a nested iframe: the shim
 * (public/geotab/addin.html) is fetched by MyGeotab and runs on *their* origin, which is
 * the only place `api.getSession()` exists. This app runs on our own origin inside that
 * shim, so the session has to come across by postMessage.
 *
 * The exchange is request/response and re-callable, not a one-shot handshake — a silent
 * re-auth after the app token expires needs a *live* session, and asking again is also how
 * we notice the user has been logged out of MyGeotab.
 */

const GEOTAB_MODE_KEY = 'GEOTAB_MODE'
const GEOTAB_PATH = '/geotab'

export const GEOTAB_REQUEST_TYPE = 'cooltrax:geotab:request'
export const GEOTAB_SESSION_TYPE = 'cooltrax:geotab:session'
export const GEOTAB_ERROR_TYPE = 'cooltrax:geotab:error'

const SESSION_TIMEOUT_MS = 15_000
/**
 * The shim attaches its message listener when MyGeotab executes the injected script, but it
 * creates our iframe in the same markup. On a refresh this app is warm-cached and can boot
 * first, so a single request would be posted into the void and then sit until the timeout.
 * Repeating it costs nothing — the shim answers whichever copy arrives once it is listening.
 */
const REQUEST_RETRY_MS = 400

export interface GeotabSession {
  database: string
  userName: string
  sessionId: string
  server: string
}

export type GeotabSessionErrorReason =
  /** Not running inside a frame at all — the add-in shim is missing. */
  | 'not-embedded'
  /** The shim never answered. */
  | 'timeout'
  /** The shim answered, but MyGeotab no longer has a session for this user. */
  | 'no-session'
  /** The reply didn't carry the fields we need. */
  | 'malformed'

export class GeotabSessionError extends Error {
  constructor(
    readonly reason: GeotabSessionErrorReason,
    message: string
  ) {
    super(message)
    this.name = 'GeotabSessionError'
  }
}

/**
 * Resolved once at module load so the mode can't flip midway through a render pass.
 * The sessionStorage flag keeps the mode after the router redirects away from /geotab.
 */
const detectGeotabMode = (): boolean => {
  if (typeof window === 'undefined') return false
  if (window.location.pathname.startsWith(GEOTAB_PATH)) {
    try {
      window.sessionStorage.setItem(GEOTAB_MODE_KEY, '1')
    } catch {
      // Storage can be unavailable in a partitioned/private context; the path check alone
      // still covers the initial load.
    }
    return true
  }
  try {
    return window.sessionStorage.getItem(GEOTAB_MODE_KEY) === '1'
  } catch {
    return false
  }
}

const geotabMode = detectGeotabMode()

export const isGeotabMode = (): boolean => geotabMode

/**
 * Only MyGeotab may hand us a session. In dev we also accept the local mock harness so the
 * flow can be exercised without a MyGeotab database.
 */
const isTrustedGeotabOrigin = (origin: string): boolean => {
  try {
    const host = new URL(origin).hostname.toLowerCase()
    if (host === 'geotab.com' || host.endsWith('.geotab.com')) return true
    return import.meta.env.DEV && (host === 'localhost' || host === '127.0.0.1')
  } catch {
    return false
  }
}

const isCompleteSession = (value: Partial<GeotabSession> | undefined): value is GeotabSession =>
  !!value &&
  typeof value.database === 'string' &&
  !!value.database &&
  typeof value.userName === 'string' &&
  !!value.userName &&
  typeof value.sessionId === 'string' &&
  !!value.sessionId &&
  typeof value.server === 'string' &&
  !!value.server

let nextRequestId = 1

/**
 * Ask the shim for the current MyGeotab session.
 *
 * Safe to call repeatedly; each call is correlated by id so a late reply can never resolve
 * a newer request. The result is deliberately not cached anywhere — hold it only for as
 * long as it takes to exchange it for an app token.
 */
export const requestGeotabSession = (): Promise<GeotabSession> =>
  new Promise<GeotabSession>((resolve, reject) => {
    if (typeof window === 'undefined' || window.parent === window) {
      reject(new GeotabSessionError('not-embedded', 'Not running inside the MyGeotab add-in frame'))
      return
    }

    const id = nextRequestId++
    let settled = false

    const settle = (action: () => void) => {
      if (settled) return
      settled = true
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
      clearInterval(retry)
      action()
    }

    function onMessage(event: MessageEvent) {
      if (!isTrustedGeotabOrigin(event.origin)) return

      const data = event.data as
        | { type?: string; id?: number; session?: Partial<GeotabSession>; reason?: string }
        | undefined
      if (!data || data.id !== id) return

      if (data.type === GEOTAB_SESSION_TYPE) {
        if (isCompleteSession(data.session)) {
          const { database, userName, sessionId, server } = data.session
          settle(() => resolve({ database, userName, sessionId, server }))
        } else {
          settle(() =>
            reject(new GeotabSessionError('malformed', 'The MyGeotab add-in returned an incomplete session'))
          )
        }
        return
      }

      if (data.type === GEOTAB_ERROR_TYPE) {
        settle(() => reject(new GeotabSessionError('no-session', data.reason || 'MyGeotab did not return a session')))
      }
    }

    const timer = setTimeout(() => {
      settle(() => reject(new GeotabSessionError('timeout', 'The MyGeotab add-in did not respond')))
    }, SESSION_TIMEOUT_MS)

    window.addEventListener('message', onMessage)

    // '*' is correct for the *request*: it carries nothing secret, and we can't know which
    // MyGeotab host is the parent (my.geotab.com, myNNN.geotab.com, a reseller domain).
    // The reply carries the sessionId and the shim sends that to our exact origin.
    const ask = () => window.parent.postMessage({ type: GEOTAB_REQUEST_TYPE, id }, '*')

    // Same id every time, so a reply to any attempt still resolves this request and a late
    // duplicate is ignored by the id check above.
    const retry = setInterval(ask, REQUEST_RETRY_MS)
    ask()
  })
